import type { UserInfo } from '../models/ReportPayload';

/**
 * Helper for retrieving user information from Office.js context.
 * Ports InfoUtils.cs functionality for the web add-in.
 */
export class UserInfoHelper {
  /**
   * Get current user information from Office mailbox.
   * @param mailbox - Office mailbox context
   * @returns User information
   */
  static getUserInfo(mailbox: Office.Mailbox): UserInfo {
    const userProfile = mailbox.userProfile;

    return {
      email: userProfile?.emailAddress || '',
      displayName: userProfile?.displayName || '',
      timeZone: userProfile?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  }

  /**
   * Get the reporter's email address.
   * Attempts multiple methods to get accurate email.
   * @param mailbox - Office mailbox context
   * @param item - Current mail item (for fallback)
   * @returns Email address string
   */
  static async getReporterEmail(mailbox: Office.Mailbox, item: Office.MessageRead): Promise<string> {
    // Primary: userProfile email
    const profileEmail = mailbox.userProfile?.emailAddress;
    if (profileEmail) {
      return profileEmail;
    }

    // Fallback: Try to get from item recipients (if user is in To/CC)
    const allRecipients = [...(item.to || []), ...(item.cc || [])];
    if (allRecipients.length > 0) {
      // Return first recipient as a reasonable guess
      return allRecipients[0].emailAddress || '';
    }

    return '';
  }

  /**
   * Build formatted user information string for the report body.
   * Matches the VSTO plugin's format.
   */
  static buildUserInfoText(userInfo: UserInfo): string {
    const lines: string[] = [];

    lines.push('=== User Information ===');
    lines.push(`Email: ${userInfo.email || 'N/A'}`);
    lines.push(`Display Name: ${userInfo.displayName || 'N/A'}`);
    lines.push(`Time Zone: ${userInfo.timeZone || 'N/A'}`);

    return lines.join('\n');
  }

  /**
   * Get plugin details for the report.
   * @param version - Plugin version string
   * @param reportsCounter - Number of reports submitted
   */
  static buildPluginDetailsText(version: string, reportsCounter: number, supportEmail?: string): string {
    const lines: string[] = [];

    lines.push('=== Plugin Details ===');
    lines.push(`Version: ${version} (Web Add-in)`);
    lines.push(`Reports Submitted: ${reportsCounter}`);
    lines.push(`Platform: ${this.getPlatformInfo()}`);
    lines.push(`Support: ${supportEmail || import.meta.env.VITE_TO_ADDRESS || 'SOC@alriyadh.gov.sa'}`);

    return lines.join('\n');
  }

  /**
   * Get basic platform/environment information.
   */
  static getPlatformInfo(): string {
    const platform = Office.context?.platform || 'Unknown';
    const host = Office.context?.host || 'Unknown';
    const diagnostics = Office.context?.diagnostics;

    let info = `${host} on ${platform}`;

    if (diagnostics) {
      info += ` (${diagnostics.host} ${diagnostics.version})`;
    }

    return info;
  }

  /**
   * Get basic system information for debugging.
   */
  static getSystemInfo(): string {
    const lines: string[] = [];

    lines.push('=== System Information ===');
    lines.push(`User Agent: ${navigator.userAgent}`);
    lines.push(`Language: ${navigator.language}`);
    lines.push(`Platform: ${navigator.platform}`);
    lines.push(`Online: ${navigator.onLine ? 'Yes' : 'No'}`);
    lines.push(`Office Platform: ${Office.context?.platform || 'Unknown'}`);
    lines.push(`Office Host: ${Office.context?.host || 'Unknown'}`);

    const diagnostics = Office.context?.diagnostics;
    if (diagnostics) {
      lines.push(`Host Version: ${diagnostics.version}`);
      lines.push(`Host Name: ${diagnostics.host}`);
    }

    return lines.join('\n');
  }
}
